import type { SurfaceAttributeValue, TaskSurfaceCard } from "@/services/api/agentic-v2/types";
import { openworkClientNameFromScenarioRef } from "@/components/clients/openworkClientSnapshots";
import { isPlaceholderClientName } from "@/components/review/saltusOnboardingReview";
import type { MatrixBucket } from "@/components/home/workloadMatrix";
import { OPENWORK_LETTER_DEMO_TAG } from "@/components/review/reviewDemoHelpers";

export type ReviewQueueAttributeConfig = {
  key: string;
  label: string;
  sortable: boolean;
  groupable: boolean;
  filterable: boolean;
  showInRow: boolean;
  nullLabel: string;
  nulls: "first" | "last";
};

export type ReviewQueueSortConfig = {
  key: string;
  direction: "asc" | "desc";
  nulls: "first" | "last";
};

export type ReviewQueueTagGroupConfig = {
  key: string;
  label: string;
  matchTags: string[];
};

export type ReviewQueueScope = "mine" | "team";

export type ReviewQueueWidgetConfig = {
  title: string;
  /** Starting Mine/Team scope; null hides the toggle (queue shows every task in the snapshot). */
  defaultScope: ReviewQueueScope | null;
  pageSize: number;
  priorities: string[] | null;
  groupBy: string | null;
  attributes: ReviewQueueAttributeConfig[];
  defaultSort: ReviewQueueSortConfig | null;
  tagGroups: ReviewQueueTagGroupConfig[];
  tagLabels: Record<string, string>;
};

export type ReviewQueueTagConfig = Pick<ReviewQueueWidgetConfig, "tagGroups" | "tagLabels">;

const REVIEW_QUEUE_HIDDEN_TAGS = new Set([
  OPENWORK_LETTER_DEMO_TAG,
  "demo",
  "user_testing",
  "rb_film_demo",
]);

const REVIEW_QUEUE_TAG_LABELS: Record<string, string> = {
  case_mortgage_protection: "Mortgage & protection",
  case_wealth: "Wealth & pensions",
  letter_main: "Letter",
  letter_addendum: "Addendum",
};

const REVIEW_QUEUE_TAG_ORDER = [
  "case_mortgage_protection",
  "case_wealth",
  "letter_main",
  "letter_addendum",
] as const;

function isOpenworkLetterDemoTask(task: TaskSurfaceCard): boolean {
  const tags = task.tags ?? [];
  return tags.includes(OPENWORK_LETTER_DEMO_TAG) || Boolean(task.scenario_ref?.startsWith("ow-"));
}

function openworkCaseTypeTag(scenarioRef: string | null | undefined): string | null {
  if (!scenarioRef) return null;
  if (scenarioRef.includes("james-sarah")) return "case_mortgage_protection";
  if (scenarioRef.includes("michael-roberts")) return "case_wealth";
  return null;
}

function openworkLetterKindTag(task: TaskSurfaceCard): string {
  return task.demo_unlock_after ? "letter_addendum" : "letter_main";
}

function reviewQueueGroupedTags(
  task: TaskSurfaceCard,
  tagGroups: ReviewQueueTagGroupConfig[],
): string[] {
  const taskTags = new Set((task.tags ?? []).map((tag) => tag?.trim()).filter(Boolean));
  const matched: string[] = [];
  for (const group of tagGroups) {
    if (group.matchTags.some((tag) => taskTags.has(tag))) {
      matched.push(group.key);
    }
  }
  return matched;
}

/** Tags shown in the home review-queue filter — hides implementation noise on OpenWork demo tasks. */
export function reviewQueueFilterTagsForTask(
  task: TaskSurfaceCard,
  tagConfig?: ReviewQueueTagConfig,
): string[] {
  if (isOpenworkLetterDemoTask(task)) {
    const tags: string[] = [];
    const caseTag = openworkCaseTypeTag(task.scenario_ref);
    if (caseTag) tags.push(caseTag);
    tags.push(openworkLetterKindTag(task));
    return tags;
  }

  if (tagConfig?.tagGroups.length) {
    return reviewQueueGroupedTags(task, tagConfig.tagGroups);
  }

  return (task.tags ?? []).filter((tag) => {
    const trimmed = tag?.trim();
    return trimmed && !REVIEW_QUEUE_HIDDEN_TAGS.has(trimmed);
  });
}

function compareReviewQueueTags(
  left: string,
  right: string,
  tagLabels?: Record<string, string>,
): number {
  const leftOrder = REVIEW_QUEUE_TAG_ORDER.indexOf(left as (typeof REVIEW_QUEUE_TAG_ORDER)[number]);
  const rightOrder = REVIEW_QUEUE_TAG_ORDER.indexOf(right as (typeof REVIEW_QUEUE_TAG_ORDER)[number]);
  if (leftOrder !== -1 || rightOrder !== -1) {
    if (leftOrder === -1) return 1;
    if (rightOrder === -1) return -1;
    return leftOrder - rightOrder;
  }
  return formatReviewQueueTagLabel(left, tagLabels).localeCompare(
    formatReviewQueueTagLabel(right, tagLabels),
  );
}

const DEFAULT_CONFIG: ReviewQueueWidgetConfig = {
  title: "Work that needs your review",
  defaultScope: null,
  pageSize: 10,
  priorities: null,
  groupBy: null,
  attributes: [],
  defaultSort: null,
  tagGroups: [],
  tagLabels: {},
};

const PRIORITY_RANK: Record<string, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

const APPROVAL_GROUP_LABEL: Record<string, string> = {
  urgent: "Urgent",
  meeting_prep: "Meeting prep",
  backlog: "To do",
};

const APPROVAL_GROUP_ORDER = ["urgent", "meeting_prep", "backlog"] as const;
const MISSING_GROUP_KEY = "__missing__";
const TASK_FIELD_SORT_KEYS = new Set(["updated_at", "queued_at"]);

export function readReviewQueueWidgetConfig(
  props: Record<string, unknown> | null | undefined,
): ReviewQueueWidgetConfig {
  const title =
    typeof props?.title === "string" && props.title.trim() ? props.title.trim() : DEFAULT_CONFIG.title;
  const pageSize =
    typeof props?.page_size === "number" && props.page_size > 0
      ? Math.floor(props.page_size)
      : DEFAULT_CONFIG.pageSize;
  const priorities = readPriorities(props?.priorities);
  const attributes = readAttributes(props?.attributes);
  const groupBy = readGroupBy(props?.group_by, attributes);
  const defaultSort = readDefaultSort(props?.default_sort, attributes);
  const tagGroups = readTagGroups(props?.tag_groups);
  const tagLabels = readTagLabels(props?.tag_labels, tagGroups);
  const defaultScope = props?.default_scope === "mine" || props?.default_scope === "team" ? props.default_scope : null;
  return { title, defaultScope, pageSize, priorities, groupBy, attributes, defaultSort, tagGroups, tagLabels };
}

export function filterReviewQueueByAssignee(
  tasks: TaskSurfaceCard[],
  scope: ReviewQueueScope | null,
  userId: string | undefined,
): TaskSurfaceCard[] {
  if (scope !== "mine" || !userId) return tasks;
  return tasks.filter((task) => task.assigned_human_id === userId);
}

function readTagGroups(value: unknown): ReviewQueueTagGroupConfig[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): ReviewQueueTagGroupConfig | null => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const key = typeof row.key === "string" ? row.key.trim() : "";
      const label = typeof row.label === "string" ? row.label.trim() : "";
      const matchTags = Array.isArray(row.match_tags)
        ? row.match_tags.filter((tag): tag is string => typeof tag === "string" && tag.trim().length > 0)
        : [];
      if (!key || !label || matchTags.length === 0) return null;
      return { key, label, matchTags };
    })
    .filter((entry): entry is ReviewQueueTagGroupConfig => entry !== null);
}

function readTagLabels(
  value: unknown,
  tagGroups: ReviewQueueTagGroupConfig[],
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const group of tagGroups) {
    labels[group.key] = group.label;
  }
  if (!value || typeof value !== "object") return labels;
  for (const [key, label] of Object.entries(value as Record<string, unknown>)) {
    if (typeof label === "string" && label.trim()) labels[key] = label.trim();
  }
  return labels;
}

function readPriorities(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const priorities = value.filter((entry): entry is string => typeof entry === "string" && entry.length > 0);
  return priorities.length > 0 ? priorities : null;
}

function readAttributes(value: unknown): ReviewQueueAttributeConfig[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): ReviewQueueAttributeConfig | null => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const key = typeof row.key === "string" ? row.key.trim() : "";
      if (!key) return null;
      return {
        key,
        label: typeof row.label === "string" && row.label.trim() ? row.label.trim() : key,
        sortable: row.sortable !== false,
        groupable: row.groupable !== false,
        filterable: row.filterable !== false,
        showInRow: row.show_in_row === true,
        nullLabel: typeof row.null_label === "string" && row.null_label.trim() ? row.null_label.trim() : "Not set",
        nulls: row.nulls === "first" ? "first" : "last",
      };
    })
    .filter((entry): entry is ReviewQueueAttributeConfig => entry !== null);
}

function readGroupBy(value: unknown, attributes: ReviewQueueAttributeConfig[]): string | null {
  if (value === "approval_group") return "approval_group";
  if (value === "letter_kind") return "letter_kind";
  if (typeof value !== "string" || !value.trim()) return null;
  const key = value.trim();
  if (attributes.some((attribute) => attribute.key === key && attribute.groupable)) return key;
  return null;
}

function readDefaultSort(
  value: unknown,
  attributes: ReviewQueueAttributeConfig[],
): ReviewQueueSortConfig | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const key = typeof row.key === "string" ? row.key.trim() : "";
  const sortableAttribute = attributes.some((attribute) => attribute.key === key && attribute.sortable);
  if (!key || (!sortableAttribute && !TASK_FIELD_SORT_KEYS.has(key))) return null;
  return {
    key,
    direction: row.direction === "desc" ? "desc" : "asc",
    nulls: row.nulls === "first" ? "first" : "last",
  };
}

export function taskSurfaceAttribute(
  task: TaskSurfaceCard,
  key: string,
): SurfaceAttributeValue | undefined {
  return task.surface_attributes?.[key];
}

export function reviewQueueRowAttributeLines(
  task: TaskSurfaceCard,
  attributes: ReviewQueueAttributeConfig[],
): { label: string; value: string }[] {
  if (!task.surface_attributes) return [];
  return attributes
    .filter((attribute) => attribute.showInRow)
    .map((attribute) => {
      const attr = taskSurfaceAttribute(task, attribute.key);
      if (!attr) return null;
      const value = attr.missing ? attribute.nullLabel : attr.display?.trim() || attribute.nullLabel;
      return { label: attribute.label, value };
    })
    .filter((line): line is { label: string; value: string } => line !== null);
}

export function formatReviewQueueTagLabel(
  tag: string,
  tagLabels?: Record<string, string>,
): string {
  if (tagLabels?.[tag]) return tagLabels[tag];
  if (REVIEW_QUEUE_TAG_LABELS[tag]) return REVIEW_QUEUE_TAG_LABELS[tag];
  return tag
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function reviewQueueTaskTags(
  tasks: TaskSurfaceCard[],
  tagConfig?: ReviewQueueTagConfig,
): string[] {
  const tags = new Set<string>();
  for (const task of tasks) {
    for (const tag of reviewQueueFilterTagsForTask(task, tagConfig)) {
      tags.add(tag);
    }
  }
  return [...tags].sort((left, right) =>
    compareReviewQueueTags(left, right, tagConfig?.tagLabels),
  );
}

export function filterReviewQueueByTag(
  tasks: TaskSurfaceCard[],
  tag: string | null,
  tagConfig?: ReviewQueueTagConfig,
): TaskSurfaceCard[] {
  if (!tag) return tasks;
  return tasks.filter((task) => reviewQueueFilterTagsForTask(task, tagConfig).includes(tag));
}

export type ReviewQueueAction =
  | { id: string; label: string; kind: "open_case" | "open_letter" }
  | { id: string; label: string; kind: "apply_action"; action: string }
  | { id: string; label: string; kind: "open_artifact"; artifactId: string }
  | { id: string; label: string; kind: "open_route"; route: string };

const SALTUS_OPS_TAGS = new Set(["saltus", "daily_ingest"]);

const SALTUS_OPS_STEP_ACTIONS: Record<string, { id: string; label: string }> = {
  capture_documents: { id: "upload", label: "Upload files" },
  ingest_ihf: { id: "open", label: "Open task" },
  cash_rec: { id: "review", label: "Review cash rec" },
  fund_rec: { id: "review", label: "Review fund rec" },
  render_pack: { id: "view_pack", label: "View pack" },
  sign_off: { id: "sign_off", label: "Review sign-off" },
  load_audit: { id: "review", label: "Review audit" },
  draft_report: { id: "review", label: "Review report" },
  ihf_reconciliation: { id: "review", label: "Review reconciliation" },
};

function isSaltusOpsTask(task: TaskSurfaceCard): boolean {
  const tags = task.tags ?? [];
  if (tags.some((tag) => SALTUS_OPS_TAGS.has(tag))) return true;
  const step = task.mission_step_key?.trim();
  return Boolean(step && step in SALTUS_OPS_STEP_ACTIONS);
}

function defaultReviewQueueActions(task: TaskSurfaceCard): ReviewQueueAction[] {
  const step = task.mission_step_key?.trim();
  const stepAction = step ? SALTUS_OPS_STEP_ACTIONS[step] : undefined;
  if (stepAction || isSaltusOpsTask(task)) {
    if (task.status === "working" || task.status === "queued") {
      return [{ id: "progress", label: "View progress", kind: "open_case" }];
    }
    const action = stepAction ?? { id: "open", label: "Open task" };
    return [{ id: action.id, label: action.label, kind: "open_case" }];
  }
  return [
    { id: "open_case", label: "Review case details", kind: "open_case" },
    { id: "open_letter", label: "Edit letter", kind: "open_letter" },
  ];
}

function resolveReviewQueueActionKind(ctaId: string, label: string): "open_case" | "open_letter" {
  const haystack = `${ctaId} ${label}`.toLowerCase();
  if (
    haystack.includes("upload") ||
    haystack.includes("capture") ||
    haystack.includes("reconcil") ||
    haystack.includes("ingest") ||
    haystack.includes("audit") ||
    haystack.includes("report") ||
    haystack.includes("sign-off") ||
    haystack.includes("sign off")
  ) {
    return "open_case";
  }
  if (
    haystack.includes("letter") ||
    haystack.includes("edit") ||
    (haystack.includes("draft") && !haystack.includes("report"))
  ) {
    return "open_letter";
  }
  return "open_case";
}

export function isFactsheetReadinessPipelineTask(task: TaskSurfaceCard): boolean {
  return isSaltusImOpsPipelineTask(task);
}

export function isSaltusReconciliationTask(task: TaskSurfaceCard): boolean {
  const tags = new Set(
    [...(task.mission_template_tags ?? []), ...(task.tags ?? [])].map((t) => t?.trim()).filter(Boolean),
  );
  if (tags.has("daily_reconcile")) return true;
  const node = (task.mission_step_key ?? task.spawned_from_node ?? "").toLowerCase();
  if (node === "cash_rec" || node === "fund_rec" || node === "reconcile_cash_and_fund" || node === "reconcile") {
    return true;
  }
  const titleLower = `${task.name ?? ""} ${task.presentation?.title ?? ""}`.toLowerCase();
  return titleLower.includes("reconcile cash and fund") || titleLower.includes("reconciliation");
}

/** Agent-automation steps for Saltus IM ops kits (Agent activity rail, not Needs you). */
export function isSaltusImOpsPipelineTask(task: TaskSurfaceCard): boolean {
  if (isSaltusReconciliationTask(task)) return false;
  const tags = new Set(
    [...(task.mission_template_tags ?? []), ...(task.tags ?? [])].map((t) => t?.trim()).filter(Boolean),
  );
  const node = task.mission_step_key ?? task.spawned_from_node ?? "";
  if (tags.has("factsheet_readiness") || tags.has("recalc_stability")) {
    if (node === "load_audit" || node === "draft_report") return true;
  }
  if (tags.has("daily_ingest")) {
    return task.status === "blocked";
  }
  if (tags.has("irec_insights") && (node === "capture_report" || node === "draft_insights")) return true;
  return false;
}

export function reviewQueueTitle(task: TaskSurfaceCard): string {
  return task.presentation?.title?.trim() || task.name?.trim() || "";
}

function clientNameFromReviewTitle(title: string | null | undefined): string | null {
  const normalized = title?.trim();
  if (!normalized) return null;
  const reviewPossessive = normalized.match(/^Review\s+(.+?)'s\b/i);
  if (reviewPossessive?.[1]?.trim()) return reviewPossessive[1].trim();
  const possessive = normalized.match(/\bfor\s+(.+?)'s\b/i);
  if (possessive?.[1]?.trim()) return possessive[1].trim();
  const trailing = normalized.match(/\bfor\s+(.+)$/i);
  return trailing?.[1]?.trim() || null;
}

export function reviewQueueClientName(task: TaskSurfaceCard): string | null {
  const fromEntity = task.entity?.display_name?.trim();
  if (fromEntity && !isPlaceholderClientName(fromEntity)) return fromEntity;

  const fromScenario = openworkClientNameFromScenarioRef(task.scenario_ref);
  if (fromScenario) return fromScenario;

  const fromTitle = clientNameFromReviewTitle(reviewQueueTitle(task));
  if (fromTitle && !isPlaceholderClientName(fromTitle)) return fromTitle;

  return null;
}

/** Secondary lead context (proposition, contact, source) for onboarding queue rows. */
export function reviewQueueLeadDetail(task: TaskSurfaceCard): string | null {
  const detail = task.entity?.lead_detail?.trim();
  return detail || null;
}

export function reviewQueueFaClientId(task: TaskSurfaceCard): string | null {
  if (task.entity?.entity_type !== "fa_client") return null;
  const id = task.entity.entity_id?.trim();
  return id || null;
}

const LEAD_CAPTURE_STAGES = new Set(["capture", "validate"]);

/** True when the linked fa_client should open in the Leads app (not Clients). */
export function reviewQueueIsLead(task: TaskSurfaceCard): boolean {
  const entity = task.entity;
  if (entity?.entity_type !== "fa_client") return false;

  if (entity.lead_lost_reason?.trim()) return true;

  const leadStatus = entity.lead_status?.trim();
  if (!leadStatus) return false;

  const stage = (task.stage ?? task.mission?.current_stage ?? "").trim().toLowerCase();
  if (stage && LEAD_CAPTURE_STAGES.has(stage)) return true;

  const normalizedStatus = leadStatus.toLowerCase();
  return normalizedStatus === "new" || normalizedStatus === "lost";
}

export function reviewQueueActions(task: TaskSurfaceCard): ReviewQueueAction[] {
  const actions: ReviewQueueAction[] = [];
  const ctas = task.presentation?.ctas?.filter((cta) => cta.label?.trim()) ?? [];

  if (ctas.length > 0) {
    for (const cta of ctas) {
      const haystack = `${cta.id} ${cta.label}`.toLowerCase();
      if (haystack.includes("view client") || haystack.includes("view_client")) continue;
      const label = cta.label.trim();
      if (cta.route) {
        actions.push({ id: cta.id, label, kind: "open_route", route: cta.route });
        continue;
      }
      if (cta.action) {
        actions.push({ id: cta.id, label, kind: "apply_action", action: cta.action });
        continue;
      }
      if (cta.artifact_id) {
        actions.push({ id: cta.id, label, kind: "open_artifact", artifactId: cta.artifact_id });
        continue;
      }
      actions.push({
        id: cta.id,
        label,
        kind: resolveReviewQueueActionKind(cta.id, cta.label),
      });
    }
  } else if (isSaltusOpsTask(task)) {
    actions.push(...defaultReviewQueueActions(task));
  }

  if (task.status === "blocked" && task.mission_id) {
    const hasClose = actions.some(
      (action) => action.kind === "apply_action" && action.action === "close_mission",
    );
    if (!hasClose) {
      actions.push({
        id: "close_mission",
        label: "Close task",
        kind: "apply_action",
        action: "close_mission",
      });
    }
  }

  return actions;
}

export function reviewQueueNote(task: TaskSurfaceCard): string {
  const ask = task.user_ask?.trim();
  if (ask) return ask;
  const summary = task.latest_summary?.trim();
  if (summary) return summary;
  const body = task.presentation?.body?.trim();
  if (body) return body;
  if (task.status === "blocked") {
    const gate = task.gate_blocked_display?.body?.trim();
    if (gate) return gate;
    const blocker = task.blockers?.[0]?.trim();
    if (blocker) return blocker;
  }
  return task.disposition_reasoning?.trim() || "";
}

export function reviewQueueStripeBucket(task: TaskSurfaceCard): MatrixBucket {
  const bucket = task.matrix_bucket;
  if (bucket === "done" || bucket === "due" || bucket === "chasing" || bucket === "stale") {
    return bucket;
  }
  if (task.status === "blocked") return "chasing";
  if (task.approval_group === "urgent") return "due";
  return "due";
}

export function filterReviewQueueTasks(
  tasks: TaskSurfaceCard[],
  searchQuery: string,
): TaskSurfaceCard[] {
  const visible = tasks.filter((task) => !isSaltusReconciliationTask(task));
  const needle = searchQuery.trim().toLowerCase();
  if (!needle) return visible;
  return visible.filter((task) => reviewQueueSearchText(task).includes(needle));
}

function reviewQueueSearchText(task: TaskSurfaceCard): string {
  const attributeText = Object.values(task.surface_attributes ?? {})
    .map((attribute) => attribute.display)
    .filter(Boolean);
  return [
    task.name,
    task.presentation?.title,
    task.presentation?.body,
    task.entity?.display_name,
    task.adviser_name,
    task.assigned_human_name,
    ...(task.tags ?? []),
    ...attributeText,
  ]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(" ")
    .toLowerCase();
}

export function filterReviewQueueByPriority(
  tasks: TaskSurfaceCard[],
  priorities: string[] | null,
): TaskSurfaceCard[] {
  if (!priorities?.length) return tasks;
  const allowed = new Set(priorities.map((value) => value.toLowerCase()));
  return tasks.filter((task) => allowed.has((task.priority || "normal").toLowerCase()));
}

function taskFieldSortValue(task: TaskSurfaceCard, key: string): string | null {
  if (key === "updated_at") return task.updated_at || null;
  if (key === "queued_at") return task.queued_at || null;
  return null;
}

function compareIsoValues(
  left: string | null,
  right: string | null,
  direction: "asc" | "desc",
  nulls: "first" | "last",
): number {
  if (!left && !right) return 0;
  if (!left) return nulls === "first" ? -1 : 1;
  if (!right) return nulls === "first" ? 1 : -1;
  const cmp = left.localeCompare(right);
  return direction === "asc" ? cmp : -cmp;
}

function compareAttributeValues(
  left: SurfaceAttributeValue | undefined,
  right: SurfaceAttributeValue | undefined,
  direction: "asc" | "desc",
  nulls: "first" | "last",
): number {
  const leftMissing = !left || left.missing || left.value == null;
  const rightMissing = !right || right.missing || right.value == null;
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return nulls === "first" ? -1 : 1;
  if (rightMissing) return nulls === "first" ? 1 : -1;
  const cmp = String(left.value).localeCompare(String(right.value));
  return direction === "asc" ? cmp : -cmp;
}

export function sortReviewQueueTasks(
  tasks: TaskSurfaceCard[],
  sort: ReviewQueueSortConfig | null,
): TaskSurfaceCard[] {
  return [...tasks].sort((a, b) => {
    const rankA = a.demo_queue_rank;
    const rankB = b.demo_queue_rank;
    const aPinned = rankA != null;
    const bPinned = rankB != null;
    if (aPinned || bPinned) {
      if (!aPinned) return 1;
      if (!bPinned) return -1;
      const rankDiff = rankA - rankB;
      if (rankDiff !== 0) return rankDiff;
    }

    if (sort) {
      const attributeCmp = TASK_FIELD_SORT_KEYS.has(sort.key)
        ? compareIsoValues(taskFieldSortValue(a, sort.key), taskFieldSortValue(b, sort.key), sort.direction, sort.nulls)
        : compareAttributeValues(
            taskSurfaceAttribute(a, sort.key),
            taskSurfaceAttribute(b, sort.key),
            sort.direction,
            sort.nulls,
          );
      if (attributeCmp !== 0) return attributeCmp;
    }

    const blockedDiff = Number(a.status === "blocked") - Number(b.status === "blocked");
    if (blockedDiff !== 0) return -blockedDiff;
    const priorityDiff =
      (PRIORITY_RANK[a.priority?.toLowerCase() ?? "normal"] ?? 9) -
      (PRIORITY_RANK[b.priority?.toLowerCase() ?? "normal"] ?? 9);
    if (priorityDiff !== 0) return priorityDiff;
    return (b.updated_at || "").localeCompare(a.updated_at || "");
  });
}

export type ReviewQueueSection = {
  key: string;
  label: string;
  tasks: TaskSurfaceCard[];
};

export function groupReviewQueueTasks(
  tasks: TaskSurfaceCard[],
  groupBy: string | null,
  attributes: ReviewQueueAttributeConfig[],
): ReviewQueueSection[] {
  if (!groupBy) {
    return [{ key: "all", label: "", tasks }];
  }

  if (groupBy === "letter_kind") {
    const main: TaskSurfaceCard[] = [];
    const addendum: TaskSurfaceCard[] = [];
    for (const task of tasks) {
      if (task.demo_unlock_after) {
        addendum.push(task);
      } else {
        main.push(task);
      }
    }
    const sections: ReviewQueueSection[] = [];
    if (main.length > 0) {
      sections.push({ key: "main", label: "Main letters", tasks: main });
    }
    if (addendum.length > 0) {
      sections.push({ key: "addendum", label: "Addendums", tasks: addendum });
    }
    return sections.length > 0 ? sections : [{ key: "all", label: "", tasks }];
  }

  if (groupBy === "approval_group") {
    const grouped = new Map<string, TaskSurfaceCard[]>();
    for (const task of tasks) {
      const key = task.approval_group || "backlog";
      const bucket = grouped.get(key) ?? [];
      bucket.push(task);
      grouped.set(key, bucket);
    }

    const sections: ReviewQueueSection[] = [];
    for (const key of APPROVAL_GROUP_ORDER) {
      const bucket = grouped.get(key);
      if (!bucket?.length) continue;
      sections.push({ key, label: APPROVAL_GROUP_LABEL[key] ?? key, tasks: bucket });
      grouped.delete(key);
    }
    for (const [key, bucket] of grouped) {
      sections.push({ key, label: APPROVAL_GROUP_LABEL[key] ?? key, tasks: bucket });
    }
    return sections;
  }

  const attribute = attributes.find((entry) => entry.key === groupBy);
  if (!attribute) {
    return [{ key: "all", label: "", tasks }];
  }

  const grouped = new Map<string, ReviewQueueSection>();
  for (const task of tasks) {
    const attr = taskSurfaceAttribute(task, attribute.key);
    const key = attr?.group_key || (attr?.missing ? MISSING_GROUP_KEY : attribute.key);
    const label = attr?.group_display || (attr?.missing ? attribute.nullLabel : attribute.label);
    const section = grouped.get(key) ?? { key, label, tasks: [] };
    section.tasks.push(task);
    grouped.set(key, section);
  }

  return [...grouped.values()].sort((left, right) => {
    if (left.key === MISSING_GROUP_KEY) return 1;
    if (right.key === MISSING_GROUP_KEY) return -1;
    return left.key.localeCompare(right.key);
  });
}
